import { Injectable } from '@angular/core';
import { Invoice } from '@shared/classes/invoice.class';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { SyncListItem } from '@shared/classes/sync-list-item.class';
import { CHECKOUT_STEPS } from '@shared/constants/checkout-steps.const';
import { SyncService } from '../sync.service';
import { LoadingService } from '../loading.service';
import { InvoicesService } from '../invoices.service';
import { CartService } from '../cart/cart.service';
import { CollectionViewService } from '../collection-view.service';
import { LogService } from '../logger/log.service';
import { SecurityService } from '../security.service';
import { catchError, filter, first, map, switchMap, timeout as rxTimeout } from 'rxjs/operators';
import { firstValueFrom, from } from 'rxjs';
import { InvoicesApiService } from '@core/services/api/invoices-api.service';
import { SecuredResponse } from '@shared/classes/secured-response.class';
import moment from 'moment';

@Injectable()
export class InvoiceProcessingService {
  private readonly logger = this.logService.createLogger('InvoiceProcessingService');
  constructor(
    private syncService: SyncService,
    private loadingService: LoadingService,
    private invoicesService: InvoicesService,
    private cartService: CartService,
    private collectionViewService: CollectionViewService,
    private securityService: SecurityService,
    private logService: LogService,
    private invoicesApiService: InvoicesApiService
  ) {}

  public async processPaidInvoice(invoice: Invoice): Promise<Invoice> {
    const company = this.securityService.getLoggedCompanyData();
    try {
      let resultInvoice: Invoice;
      if (company.isRequireOnlineInvoiceProcessing) {
        const timeoutMs = company.isKassenSichVEnabled || company.isRKSVEnabled ? 5000 : 30000;
        resultInvoice = await this.finishInvoicePaymentInOnline(invoice, timeoutMs);
      } else {
        resultInvoice = await this.finishInvoicePaymentRegularFlow(invoice, false);
      }
      return new Invoice(resultInvoice);
    } catch {
      this.finishInvoicePaymentRegularFlow(invoice, false).catch((error) => this.logger.error(error, 'processPaidInvoice'));
      throw undefined;
    }
  }

  private finishInvoicePaymentInOnline(invoice: Invoice, timeoutMs: number): Promise<Invoice> {
    this.loadingService.showLoadingItem(null, null, true);
    return firstValueFrom(
      from(this.invoicesService.saveInvoice(invoice, true)).pipe(
        switchMap(() => from(this.syncService.addDataToSyncList(new SyncListItem(invoice, UPDATES_TYPES.Invoice.type)))),
        switchMap(() =>
          this.syncService.dataToSaveSubject.pipe(
            filter((item) => item.type === UPDATES_TYPES.Invoice.type),
            map((item) => new Invoice(item?.properties)),
            filter((item) => item?.uuid === invoice?.uuid && !!item.sequentId),
            first(),
            rxTimeout(timeoutMs),
          )
        ),
        switchMap((syncedInvoice) => {
          this.logger.debug('finishInvoicePaymentInOnline:invoiceSynced', { invoiceUuid: syncedInvoice?.uuid });
          return from(this.finishInvoicePaymentRegularFlow(syncedInvoice, true));
        }),
        catchError(() => {
          this.logger.debug('finishInvoicePaymentInOnline:timeout, falling back to regular flow');
          return from(this.finishInvoicePaymentRegularFlow(invoice, false));
        })
      )
    );
  }

  private finishInvoicePaymentRegularFlow(invoice: Invoice, withoutSync: boolean): Promise<Invoice> {
    this.cartService.setActiveInvoice(invoice);
    this.collectionViewService.setCurrentSidebarSlide(CHECKOUT_STEPS.SUCCESS);
    this.loadingService.hideLoadingItem();
    return this.tryImmediatelyUpdatePaidInvoice(invoice, withoutSync);
  }

  private async tryImmediatelyUpdatePaidInvoice(invoice: Invoice, withoutSync: boolean): Promise<Invoice> {
    const invoiceToServer: Invoice = new Invoice({
      ...invoice,
      modificationDate: moment.utc().toISOString()
    });

    try {

      const response: SecuredResponse = await this.invoicesApiService.upsertInvoice(invoiceToServer);

      if (response && (response?.status === 200 || response?.status === 201) && Boolean(response?.data)) {
        return this.invoicesService.saveInvoice(invoice, true);
      } else {
        return this.invoicesService.saveInvoice(invoice, withoutSync);
      }

    } catch (error) {

      this.logger.error(error, 'tryImmediatelyUpdatePaidInvoice.generalError');
      return this.invoicesService.saveInvoice(invoice, withoutSync);

    }

  }
}
